import {
  Component,
  Input,
  ElementRef,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-sparkline',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './sparkline.component.html',
  styleUrl: './sparkline.component.scss',
})
export class SparklineComponent implements AfterViewInit, OnChanges {
  @Input() data: number[] = [];
  @Input() labels: string[] = [];
  @Input() width: number = 320;
  @Input() height: number = 92;
  @Input() strokeWidth: number = 2.2;
  @Input() showLabels: boolean = true;
  @Input() showDots: boolean = true;
  @Input() aspectRatio: string = 'xMidYMid meet';

  dotPositions: { cx: number; cy: number }[] = [];
  labelPositions: { x: number; y: number; text: string; show: boolean }[] = [];

  private initialized = false;

  constructor(private elementRef: ElementRef) {}

  ngAfterViewInit() {
    this.initialized = true;
    setTimeout(() => {
      this.renderSparkline();
    }, 0);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.initialized && changes['data']) {
      this.renderSparkline();
    }
  }

  renderSparkline() {
    if (!this.data || this.data.length === 0) {
      return;
    }

    const svg = this.elementRef.nativeElement.querySelector('svg');
    if (!svg) return;

    const labelHeight = this.showLabels && this.labels.length > 0 ? 24 : 0;
    const graphHeight = this.height - labelHeight;
    const w = this.width;
    const min = Math.min(...this.data);
    const max = Math.max(...this.data);
    const step = (w - 6) / (this.data.length - 1 || 1);
    const y = (v: number) =>
      graphHeight - ((v - min) / (max - min || 1)) * (graphHeight - 8) - 4;

    let pathData = `M3 ${y(this.data[0])}`;
    this.dotPositions = [];
    this.labelPositions = [];

    const minLabelSpacing = 35;
    const shouldShowLabel = this.calculateLabelVisibility(
      this.data.length,
      step,
      minLabelSpacing
    );

    for (let i = 0; i < this.data.length; i++) {
      const xPos = 3 + i * step;
      const yPos = y(this.data[i]);

      if (i > 0) {
        pathData += ` L${xPos} ${yPos}`;
      }

      if (this.showDots) {
        this.dotPositions.push({ cx: xPos, cy: yPos });
      }

      if (this.showLabels && this.labels.length > i) {
        this.labelPositions.push({
          x: xPos,
          y: graphHeight + 16,
          text: this.formatLabel(this.labels[i]),
          show: shouldShowLabel(i),
        });
      }
    }

    const path = svg.querySelector('path');
    if (path) {
      path.setAttribute('d', pathData);
    }
  }

  calculateLabelVisibility(
    dataLength: number,
    step: number,
    minSpacing: number
  ): (index: number) => boolean {
    if (step >= minSpacing) {
      return () => true;
    }

    const interval = Math.ceil(minSpacing / step);

    return (index: number) => {
      if (index === 0 || index === dataLength - 1) {
        return true;
      }
      return index % interval === 0;
    };
  }

  formatLabel(dateString: string): string {
    if (!dateString) return '';

    const date = new Date(dateString);
    const month = date.toLocaleString('en-US', { month: 'short' });
    const day = date.getDate();

    return `${month} ${day}`;
  }
}
